import React from "react";
import { AlertMessage, IAlertMessageProps, Spinner } from "component-library";

import { EmailForm, IEmailForm, IEmailFormContent } from "../EmailForm";
import { UserClaimTypes, UserManager } from "core";
import { IEmail } from "models";

import "./ShareViaEmail.scss";
import { getUserAttribute, UserAttributes } from "shared-auth";

export interface IShareViaEmailContent extends IEmailFormContent {
  emailFormHeader: string;
  emailFormSubmitButton: string;
  emailFormCancelButton: string;
  emailFormSubjectTemplate: string;
}

export interface IShareViaEmailProps {
  content: IShareViaEmailContent;
  sendEmail: (email: IEmail) => Promise<void>;
  closeForm: () => void;
}

interface IShareViaEmailState {
  form: IEmailForm;
  isFormSubmitted: boolean;
  isFormValid: boolean;
  showLoader: boolean;
}

export default class ShareViaEmail extends React.Component<
  IShareViaEmailProps,
  IShareViaEmailState
> {
  private defaultEmailForm: IEmailForm;

  constructor(props: IShareViaEmailProps) {
    super(props);

    const performerName = UserManager.getClaimWithDefault(
      UserClaimTypes.profileFullName
    );
    const defaultSubject = props.content.emailFormSubjectTemplate.replace(
      "{performerName}",
      performerName
    );

    this.defaultEmailForm = {
      message: "",
      subject: defaultSubject,
      senderName: getUserAttribute(UserAttributes.FullName),
      senderEmail: getUserAttribute(UserAttributes.Email),
      recipients: "",
    };

    this.state = {
      form: { ...this.defaultEmailForm },
      isFormSubmitted: false,
      isFormValid: false,
      showLoader: false,
    };
  }

  public render() {
    const { content, closeForm } = this.props;
    const { isFormSubmitted, showLoader } = this.state;

    const alertProps = this.getAlertProps(content, this.sendEmail, closeForm);

    return (
      <div className="c-share-via-email">
        <AlertMessage {...alertProps}>
          <EmailForm
            updateForm={this.updateForm}
            updateFormValidity={this.updateFormValidity}
            initialForm={this.defaultEmailForm}
            content={content}
            isFormSubmitted={isFormSubmitted}
          />
        </AlertMessage>
        {showLoader && this.renderSpinner()}
      </div>
    );
  }

  private renderSpinner = () => (
    <div className="c-share-via-email__spinner">
      <Spinner />
    </div>
  );

  private getAlertProps = (
    content: IShareViaEmailContent,
    sendEmail: () => void,
    closeForm: () => void
  ): IAlertMessageProps => {
    const result: IAlertMessageProps = {
      texts: {
        title: content.emailFormHeader,
      },
      buttons: [
        {
          name: content.emailFormSubmitButton,
          type: "primary",
          click: sendEmail,
        },
        {
          name: content.emailFormCancelButton,
          type: "secondary",
          click: closeForm,
        },
      ],
    };

    return result;
  };

  private updateFormValidity = (isFormValid: boolean) =>
    this.setState({ isFormValid });

  private updateForm = (form: IEmailForm) => this.setState({ form });

  private sendEmail = () => {
    this.setState({ isFormSubmitted: true }, () => {
      const { form, isFormValid } = this.state;
      const { sendEmail, closeForm } = this.props;

      if (isFormValid) {
        this.setState({ showLoader: true }, async () => {
          const email = this.convertFormToEmail(form);
          await sendEmail(email);
          closeForm();
        });
      }
    });
  };

  private convertFormToEmail = (form: IEmailForm): IEmail => {
    const recipients = form.recipients
      .split(",")
      .map((email) => email.trim())
      .filter((email) => Boolean(email));

    const result: IEmail = {
      ...form,
      recipients,
    };

    return result;
  };
}
